import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Save, Stethoscope } from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import PartnerCredentialsModal from './PartnerCredentialsModal';

const SPECIALTIES = [
  'General Physician', 'Cardiology', 'Orthopedics', 'Gynecology', 'Pediatrics',
  'Diabetology', 'Ophthalmology', 'ENT', 'Dermatology', 'Neurology', 'Urology',
  'Nephrology', 'Oncology', 'Psychiatry', 'Dentistry', 'Radiology', 'Other',
];

const EMPTY = {
  name: '', specialty: '', clinic_or_hospital: '', city: '',
  mobile: '', alt_mobile: '', email: '', registration_number: '',
  referral_incentive_pct: 10, is_verified: false, notes: '',
};

export default function NewReferringDoctor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { create: apiCreate, update: apiUpdate, fetch: apiFetch } = useApiMethods({ throwOnFail: true });
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState(null);
  const [credsModal, setCredsModal] = useState(null);   // { creds, doctorId, name }
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await apiFetch(`engage/referring-doctors/${id}/`);
        const d = res?.data || res?.data?.data;
        if (d) setForm({ ...EMPTY, ...d });
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load doctor');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim() || saving) return;
    setSaving(true); setError(null);
    try {
      if (isEdit) {
        await apiUpdate(`/engage/referring-doctors/${id}/`, form);
        navigate(`/engage/referring-doctors/${id}`);
      } else {
        const resp = await apiCreate('/engage/referring-doctors/', form);
        const created = resp?.data || resp?.data?.data;
        const generatedCreds = resp?.generated_credentials || resp?.data?.generated_credentials;
        if (generatedCreds?.created && generatedCreds?.password) {
          // Show the one-time credentials modal before navigating away
          setSaving(false);
          setCredsModal({ creds: generatedCreds, doctorId: created?.id, name: created?.name || form.name });
          return;
        }
        navigate(created?.id ? `/engage/referring-doctors/${created.id}` : '/engage/referring-doctors');
      }
    } catch (err) {
      setError(err?.response?.data?.message || (err?.response?.data && JSON.stringify(err.response.data)) || 'Failed to save');
      setSaving(false);
    }
  };

  const backTo = isEdit ? `/engage/referring-doctors/${id}` : '/engage/referring-doctors';
  const INPUT = "w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500";
  const L = ({ children }) => <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{children}</label>;

  if (loading) return <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading…</div>;

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-4">
      <button onClick={() => navigate(backTo)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white shadow-sm"><Stethoscope className="w-5 h-5" /></div>
        <div><h1 className="text-xl font-bold text-gray-900 dark:text-white">{isEdit ? 'Edit' : 'Add'} Referring Doctor</h1></div>
      </div>
      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}
      <form onSubmit={submit} className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><L>Doctor name <span className="text-red-500">*</span></L><input required value={form.name} onChange={e => set('name', e.target.value)} className={INPUT} placeholder="Dr. Ramesh Kumar" /></div>
          <div><L>Mobile <span className="text-red-500">*</span></L><input required value={form.mobile} onChange={e => set('mobile', e.target.value)} className={INPUT} placeholder="+91…" /></div>
          <div>
            <L>Specialty</L>
            <select value={form.specialty} onChange={e => set('specialty', e.target.value)} className={INPUT}>
              <option value="">— Select —</option>
              {SPECIALTIES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div><L>Registration number</L><input value={form.registration_number} onChange={e => set('registration_number', e.target.value)} className={INPUT} placeholder="MCI / State reg no." /></div>
          <div><L>Clinic / Hospital</L><input value={form.clinic_or_hospital} onChange={e => set('clinic_or_hospital', e.target.value)} className={INPUT} /></div>
          <div><L>City</L><input value={form.city} onChange={e => set('city', e.target.value)} className={INPUT} /></div>
          <div><L>Alt mobile</L><input value={form.alt_mobile} onChange={e => set('alt_mobile', e.target.value)} className={INPUT} /></div>
          <div><L>Email</L><input type="email" value={form.email} onChange={e => set('email', e.target.value)} className={INPUT} /></div>
          <div><L>Referral incentive %</L><input type="number" value={form.referral_incentive_pct} onChange={e => set('referral_incentive_pct', e.target.value)} className={INPUT} /></div>
          <div className="flex items-center gap-2 pt-6">
            <input type="checkbox" id="verified" checked={!!form.is_verified} onChange={e => set('is_verified', e.target.checked)} className="w-4 h-4" />
            <label htmlFor="verified" className="text-sm text-gray-700 dark:text-gray-300">Verified doctor</label>
          </div>
        </div>
        <div><L>Notes</L><textarea rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} className={INPUT} /></div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
          <button type="button" onClick={() => navigate(backTo)} className="px-4 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
          <button type="submit" disabled={saving || !form.name.trim() || !form.mobile.trim()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm shadow-sm hover:bg-purple-700 disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Doctor')}
          </button>
        </div>
      </form>

      {credsModal && (
        <PartnerCredentialsModal
          partnerName={credsModal.name}
          partnerKind="doctor"
          credentials={credsModal.creds}
          onClose={() => {
            const dest = credsModal.doctorId ? `/engage/referring-doctors/${credsModal.doctorId}` : '/engage/referring-doctors';
            setCredsModal(null);
            navigate(dest);
          }}
        />
      )}
    </div>
  );
}
